import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import type { Resolution } from "../resolution/pick-resolution";
import { selectWindow } from "../stream-cache/select-window";
import { type CachedStream, getStreamCacheKey, useStreamCacheStore } from "../stream-cache/stream-cache-store";
import type { RequestedStream, WindowedStreams } from "./types";

type StreamCacheSlice = Record<string, CachedStream | undefined>;
type StreamCacheState = ReturnType<typeof useStreamCacheStore.getState>;

function buildStreamCacheSlice(
    streamCache: StreamCacheState["streamCache"],
    lookupKeys: ReturnType<typeof getStreamCacheKey>[],
): StreamCacheSlice {
    return Object.fromEntries(lookupKeys.map((key) => [key, streamCache[key]]));
}

/** Reads this panel's cache and window-selects points. See README.md. */
export function useStreamData({
    streams,
    startMs,
    endMs,
    resolution,
}: {
    streams: RequestedStream[];
    startMs: number;
    endMs: number;
    resolution: Resolution;
}): WindowedStreams {
    const streamIds = streams.map((s) => s.streamId);
    const streamsKey = [...streamIds].sort((a, b) => a - b).join(",");

    const lookupKeys = useMemo(
        () => streamIds.map((id) => getStreamCacheKey(id, resolution)),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- streamsKey stabilizes streamIds
        [streamsKey, resolution],
    );

    // Tag with streamIds + resolution — useShallow can't tell two all-undefined slices apart otherwise.
    const lookupId = `${streamsKey}:${resolution}`;

    const selector = useCallback(
        (state: StreamCacheState) => ({
            _lookupId: lookupId,
            ...buildStreamCacheSlice(state.streamCache, lookupKeys),
        }),
        [lookupKeys, lookupId],
    );

    const sliceWithSentinel = useStreamCacheStore(useShallow(selector));

    // Drop the tag — only needed for the shallow compare above.
    const slice = useMemo<StreamCacheSlice>(() => {
        const { _lookupId: _sentinel, ...rest } = sliceWithSentinel;
        return rest;
    }, [sliceWithSentinel]);

    // Re-slice when the user pans (startMs/endMs). Cache subscription stays the same.
    return useMemo(() => {
        const data: WindowedStreams = {};
        streamIds.forEach((id) => {
            const entry = slice[getStreamCacheKey(id, resolution)];
            if (entry) {
                data[id] = { points: selectWindow(entry.points, startMs, endMs) };
            }
        });
        return data;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- streamsKey stabilizes streamIds
    }, [slice, startMs, endMs, resolution, streamsKey]);
}
